import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import {
  IconButton,
  Icons,
  useDisruptiveActionDialog,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import type { ChatHistoryItem } from '@cast/types';

dayjs.extend(relativeTime);

const chatRowStyles = (isActive: boolean) => ({
  p: 8,
  borderRadius: '4px',
  cursor: 'pointer',
  transition: 'background-color 0.15s',
  background: isActive
    ? tokens.theme.bg.structural.surfaceRaised
    : 'transparent',
  '&:hover': {
    background: isActive
      ? tokens.theme.bg.structural.surfaceRaised
      : tokens.theme.bg.structural.surfaceSubtle,
  },
});

type ChatHistoryListProps = {
  history: ChatHistoryItem[];
  onSelectChat: (chatId: string) => void;
  onDeleteChat: (chatId: string) => void;
  onNewChat?: () => void;
  currentChatId?: string;
};

export const ChatHistoryList = ({
  history,
  onSelectChat,
  onDeleteChat,
  onNewChat,
  currentChatId,
}: ChatHistoryListProps) => {
  const isNewChatActive = !currentChatId;
  const { openDisruptiveActionDialog } = useDisruptiveActionDialog();

  const handleDeleteClick = (chatId: string) => {
    openDisruptiveActionDialog({
      title: 'Delete chat?',
      body: 'This action cannot be undone.',
      onProceed: () => {
        onDeleteChat(chatId);
      },
    });
  };

  return (
    <Stack
      gap={2}
      sx={{ p: 8, height: '100%' }}
      data-testid="chat-history-list"
    >
      {/* New Chat Entry */}
      {onNewChat && (
        <Stack
          direction="row"
          alignItems="center"
          gap={8}
          sx={chatRowStyles(isNewChatActive)}
          onClick={onNewChat}
          data-testid="chat-history-new-chat-item"
        >
          <Typography
            variant="P14R"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            New chat
          </Typography>
        </Stack>
      )}

      {history.length === 0 ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          data-testid="chat-history-empty-state"
          sx={{
            flex: 1,
            px: 24,
            textAlign: 'center',
          }}
        >
          <Stack gap={8} alignItems="center">
            <Icons.ClockCounterClockwise size={48} weight="thin" />
            <Stack gap={4}>
              <Typography
                variant="P16B"
                color={tokens.theme.fg.structural.primary}
              >
                No chat history yet
              </Typography>
              <Typography
                variant="P14R"
                color={tokens.theme.fg.structural.secondary}
              >
                Your conversations will appear here once you start chatting
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      ) : (
        history.map((item) => {
          const isActive = item.id === currentChatId;

          return (
            <Stack
              key={item.id}
              direction="row"
              alignItems="center"
              gap={8}
              sx={{
                ...chatRowStyles(isActive),
                position: 'relative',
              }}
              onClick={() => onSelectChat(item.id)}
              data-testid={`chat-history-item-${item.id}`}
            >
              <Typography
                variant="P14R"
                color={tokens.theme.fg.structural.primary}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {item.title}
              </Typography>

              <IconButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(item.id);
                }}
                aria-label="Delete chat"
                data-testid={`chat-history-delete-${item.id}`}
                sx={{
                  visibility: isActive ? 'visible' : 'hidden', // prevent layout shift
                  pointerEvents: isActive ? 'auto' : 'none',
                  '&:hover': {
                    background: tokens.theme.bg.structural.surfaceSubtle,
                  },
                }}
              >
                <Icons.Trash size={16} color={tokens.theme.fg.action.onGhost} />
              </IconButton>
            </Stack>
          );
        })
      )}
    </Stack>
  );
};
